/**
 * Registry der KV-Vorlagen (AP-22 Teil 6, K1) und die beiden Fragen, die man an eine
 * Vorlage stellt: Was gilt an einem Stichtag — und was ändert sich danach.
 */
import { AT_GESETZ } from './gesetz'
import { AT_METALLGEWERBE_ARBEITER } from './metallgewerbe-arbeiter'
import { EINSTELLUNGSKATALOG, type EinstellungDef } from '../einstellungskatalog'
import { zulageSchluessel, type KvVorlage } from './typen'

export { zulageSchluessel } from './typen'
export type { KvVorlage, KvWertsatz, ZulageDef } from './typen'
export { BESTANDSWERTE, BESTAND_GUELTIG_AB } from './bestand'

/** Alle Vorlagen, in der Reihenfolge der Auswahl. */
export const KV_VORLAGEN: readonly KvVorlage[] = [AT_METALLGEWERBE_ARBEITER, AT_GESETZ]

/**
 * Die Vorlage, die gilt, wo keine gewählt ist. Die Vorgaben des Rechenkerns **waren** bis
 * Teil 6 dieser Vertrag — wer nichts wählt, rechnet deshalb weiter damit.
 */
export const KV_STANDARD = AT_METALLGEWERBE_ARBEITER.schluessel

const JE_SCHLUESSEL = new Map(KV_VORLAGEN.map((v) => [v.schluessel, v]))

export function istKvVorlage(schluessel: string): boolean {
  return JE_SCHLUESSEL.has(schluessel)
}

/** Die Vorlage zum Schlüssel; ohne oder mit unbekanntem Schlüssel die Standardvorlage. */
export function kvVorlage(schluessel?: string | null): KvVorlage {
  return (schluessel ? JE_SCHLUESSEL.get(schluessel) : undefined) ?? AT_METALLGEWERBE_ARBEITER
}

/**
 * Die Werte der Vorlage an einem Stichtag: alle Wertsätze mit `gueltigAb <= stichtag`,
 * in ihrer Reihenfolge übereinandergelegt — ein späterer Satz ersetzt nur, was er trägt.
 */
export function vorlageWerte(vorlage: KvVorlage, stichtag: string): Record<string, string> {
  const werte: Record<string, string> = {}
  for (const satz of vorlage.wertsaetze) {
    if (satz.gueltigAb > stichtag) continue
    Object.assign(werte, satz.werte)
  }
  return werte
}

/**
 * Der nächste Wechsel eines Werts **nach** dem Stichtag — damit die Maske ankündigen kann,
 * dass der KV-Standard sich zum Jahreswechsel ändert. `null`, wenn keiner vorgemerkt ist.
 */
export function vorlageNaechsterWert(vorlage: KvVorlage, key: string, stichtag: string): { gueltigAb: string; wert: string } | null {
  for (const satz of vorlage.wertsaetze) {
    if (satz.gueltigAb > stichtag && key in satz.werte) return { gueltigAb: satz.gueltigAb, wert: satz.werte[key] }
  }
  return null
}

/**
 * Der Einstellungskatalog einer Vorlage: die Schlüssel, die jeder Vertrag hat, und dazu je
 * Zulage ihres Katalogs Schalter und Satz (bei Zulagen mit Uhrzeitfenster auch dessen Grenzen).
 */
export function einstellungskatalogFuer(vorlage: KvVorlage): EinstellungDef[] {
  const zulagen = vorlage.zulagen.flatMap((z): EinstellungDef[] => {
    const k = zulageSchluessel(z.schluessel)
    return [
      { key: k.aktiv, label: z.name, art: 'schalter', gruppe: 'zulagen' },
      { key: k.satz, label: `${z.name}: Satz`, art: 'zahl', gruppe: 'zulagen', einheit: z.prozent ? '%' : `€/${z.einheit}` },
      ...(z.bemessung === 'fensterstunden_ohne_ueberstunden'
        ? [
            { key: k.von, label: `${z.name}: von`, art: 'text' as const, gruppe: 'zulagen' as const },
            { key: k.bis, label: `${z.name}: bis`, art: 'text' as const, gruppe: 'zulagen' as const },
          ]
        : []),
    ]
  })
  return [...EINSTELLUNGSKATALOG, ...zulagen]
}
